import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { LayoutGrid, ChevronLeft, ChevronRight, RefreshCw, Search, ExternalLink, Loader2, AlertTriangle, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { contentCalendarAPI, teamAPI, serviceDeliveryAPI, workflowSettingsAPI } from '../lib/api';
import { CONTENT_STATES, getStateMeta, CONTENT_PILLARS, matchPillar, getPillarMeta } from '../lib/contentStates';
import { ChannelIconsRow } from './ChannelIcons';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Checkbox } from './ui/checkbox';
import { CommentsBadge } from './InternalCommentsThread';
import { internalCommentsAPI } from '../lib/api';
import { PostDownloadMenu, DownloadSelectionBar, useDownloadSelection } from './PostDownloadTools';
import { TeamWorkload } from './TeamWorkload';

const POLL_INTERVAL_MS = 15000;
const SPANISH_MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DOW_LETTERS = ['L','M','X','J','V','S','D'];

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
// Monday-first weekday index: 0=Mon..6=Sun
function dowIndex(y, m, d) { return (new Date(y, m, d).getDay() + 6) % 7; }
function ymd(y, m, d) { return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`; }
const OVERDUE_FILTER = '__overdue__';
function isOverdue(p, todayStr) {
  const date = (p.fecha || '').slice(0, 10);
  return !!date && date < todayStr && p.estado !== 'publicado';
}

export default function PublicationsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canConfigure = ['ceo', 'manager'].includes(user?.role);
  // Todo el equipo interno puede cambiar el estado de cualquier publicación (trabajo cruzado)
  const canEditPost = (p) => !!user && user.role !== 'client';
  const [posts, setPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [schedulerUserId, setSchedulerUserId] = useState('');
  const [reviewerUserId, setReviewerUserId] = useState('');
  const [roleUsers, setRoleUsers] = useState({ editor_user_id: '', designer_user_id: '' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [clientFilter, setClientFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [responsableFilter, setResponsableFilter] = useState('');
  const [autoFilter, setAutoFilter] = useState(''); // '' | 'con' | 'sin'
  const [pilarFilter, setPilarFilter] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const isMounted = useRef(true);
  const selection = useDownloadSelection();
  const [commentInfo, setCommentInfo] = useState({});
  useEffect(() => { internalCommentsAPI.unread().then((r) => setCommentInfo(r || {})).catch(() => {}); }, []);

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [postsData, clientsData, teamData] = await Promise.all([
        contentCalendarAPI.getAll(),
        serviceDeliveryAPI.getClients().catch(() => []),
        teamAPI.getAll().catch(() => []),
      ]);
      if (!isMounted.current) return;
      setPosts(Array.isArray(postsData) ? postsData : []);
      setClients(Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []));
      setTeam(Array.isArray(teamData) ? teamData : (teamData?.members || []));
      setLastUpdated(new Date());
      if (canConfigure) {
        workflowSettingsAPI.get().then((s) => {
          setSchedulerUserId(s?.scheduler_user_id || '');
          setReviewerUserId(s?.reviewer_user_id || '');
          setRoleUsers({ editor_user_id: s?.editor_user_id || '', designer_user_id: s?.designer_user_id || '' });
        }).catch(() => {});
      }
    } catch (e) {
      if (!silent) toast.error('Error cargando matriz');
    } finally {
      if (!isMounted.current) return;
      setLoading(false); setRefreshing(false);
    }
  }, [canConfigure]);

  useEffect(() => {
    isMounted.current = true;
    loadAll();
    const id = setInterval(() => loadAll(true), POLL_INTERVAL_MS);
    return () => { isMounted.current = false; clearInterval(id); };
  }, [loadAll]);

  const goPrev = () => setCursor((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 });
  const goNext = () => setCursor((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 });
  const goToday = () => { const n = new Date(); setCursor({ year: n.getFullYear(), month: n.getMonth() }); };

  const filtersActive = !!stateFilter || !!responsableFilter || !!autoFilter || !!pilarFilter;
  const todayStr = (() => { const n = new Date(); return ymd(n.getFullYear(), n.getMonth(), n.getDate()); })();

  // Matrix: clients × days. Build a Map<clientId, Map<dateStr, post[]>>
  // Applies the active state/responsable filters.
  const matrix = useMemo(() => {
    const m = {};
    for (const p of posts) {
      if (stateFilter === OVERDUE_FILTER) {
        if (!isOverdue(p, todayStr)) continue;
      } else if (stateFilter && p.estado !== stateFilter) continue;
      if (responsableFilter && (p.responsable_id || '') !== responsableFilter) continue;
      if (autoFilter === 'con' && !p.automatizacion) continue;
      if (autoFilter === 'sin' && p.automatizacion) continue;
      if (pilarFilter && matchPillar(p.pilar)?.id !== pilarFilter) continue;
      const cid = p.client_id || '__no_client__';
      const date = (p.fecha || '').slice(0, 10);
      if (!date) continue;
      if (!m[cid]) m[cid] = {};
      if (!m[cid][date]) m[cid][date] = [];
      m[cid][date].push(p);
    }
    return m;
  }, [posts, stateFilter, responsableFilter, autoFilter, pilarFilter, todayStr]);

  const monthPrefix = `${cursor.year}-${String(cursor.month+1).padStart(2,'0')}`;

  const filteredClients = useMemo(() => {
    const lc = clientFilter.trim().toLowerCase();
    let list = clients
      .map((c) => ({ id: c._id || c.id, name: c.name || c.nombre || 'Sin nombre' }))
      .filter((c) => !lc || c.name.toLowerCase().includes(lc));
    // When filtering by state/responsable, hide clients with no matching posts in the visible month
    if (filtersActive) {
      list = list.filter((c) => {
        const byDate = matrix[c.id] || {};
        return Object.keys(byDate).some((d) => d.startsWith(monthPrefix) && byDate[d].length > 0);
      });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [clients, clientFilter, filtersActive, matrix, monthPrefix]);

  const visiblePosts = useMemo(() => {
    const out = [];
    for (const c of filteredClients) {
      const byDate = matrix[c.id] || {};
      for (const d of Object.keys(byDate).sort()) if (d.startsWith(monthPrefix)) out.push(...byDate[d]);
    }
    return out;
  }, [filteredClients, matrix, monthPrefix]);

  const totalDays = daysInMonth(cursor.year, cursor.month);
  const dayList = Array.from({ length: totalDays }, (_, i) => i + 1);

  const updatePost = async (postId, patch) => {
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, ...patch } : p));
    try {
      const updated = await contentCalendarAPI.update(postId, patch);
      setPosts((prev) => prev.map((p) => p.id === postId ? updated : p));
    } catch (e) {
      toast.error(e?.message || 'Error al actualizar');
      loadAll(true);
    }
  };

  const openInCalendar = (post) => {
    if (!post?.client_id) {
      toast.error('Esta publicación no tiene cliente asignado');
      return;
    }
    navigate('/service-delivery', {
      state: {
        openClientId: post.client_id,
        openPostId: post.id,
        openSection: 'calendario',
      },
    });
  };

  const teamOptions = useMemo(
    () => (team || []).map((m) => ({ id: m.id, name: m.name || m.username || 'Sin nombre' })),
    [team]
  );

  const saveScheduler = async (value) => {
    const v = value === '__none__' ? null : value;
    setSchedulerUserId(v || '');
    try {
      await workflowSettingsAPI.update({ scheduler_user_id: v });
      toast.success('Responsable de programación actualizado');
    } catch (e) {
      toast.error(e?.message || 'No se pudo guardar el ajuste');
    }
  };

  const saveReviewer = async (value) => {
    const v = value === '__none__' ? null : value;
    setReviewerUserId(v || '');
    try {
      await workflowSettingsAPI.update({ reviewer_user_id: v });
      toast.success('Responsable de revisión actualizado');
    } catch (e) {
      toast.error(e?.message || 'No se pudo guardar el ajuste');
    }
  };

  const saveRoleUser = async (field, label, value) => {
    const v = value === '__none__' ? null : value;
    setRoleUsers((r) => ({ ...r, [field]: v || '' }));
    try {
      await workflowSettingsAPI.update({ [field]: v });
      toast.success(`Responsable de ${label} actualizado`);
    } catch (e) {
      toast.error(e?.message || 'No se pudo guardar el ajuste');
    }
  };

  // Counts per state (independent of the active state filter, so every chip shows its total).
  // Respects the client-name search and the responsable filter + current month.
  const stateCounts = useMemo(() => {
    const counts = {};
    const lc = clientFilter.trim().toLowerCase();
    const nameById = {};
    for (const c of clients) nameById[c._id || c.id] = (c.name || c.nombre || '').toLowerCase();
    let overdue = 0;
    for (const p of posts) {
      const d = (p.fecha || '').slice(0, 7);
      if (d !== monthPrefix) continue;
      if (responsableFilter && (p.responsable_id || '') !== responsableFilter) continue;
      if (lc) {
        const n = nameById[p.client_id] || '';
        if (!n.includes(lc)) continue;
      }
      counts[p.estado] = (counts[p.estado] || 0) + 1;
      if (isOverdue(p, todayStr)) overdue += 1;
    }
    counts.__overdue__ = overdue;
    return counts;
  }, [posts, clients, clientFilter, responsableFilter, monthPrefix, todayStr]);

  const totalVisible = useMemo(() => Object.entries(stateCounts).reduce((a, [k, v]) => k === '__overdue__' ? a : a + v, 0), [stateCounts]);
  const overdueCount = stateCounts.__overdue__ || 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="publications-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <LayoutGrid className="w-7 h-7 text-primary" /> Control de Publicaciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Matriz mensual · {filteredClients.length} {filteredClients.length === 1 ? 'cliente' : 'clientes'} × {totalDays} días
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && <span className="text-xs text-muted-foreground">Actualizado: {lastUpdated.toLocaleTimeString('es-ES')}</span>}
          <Button variant="outline" size="sm" onClick={() => loadAll()} disabled={loading || refreshing} data-testid="publications-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>
      </div>

      <TeamWorkload refreshKey={lastUpdated?.getTime()} onOpenPost={openInCalendar} />

      {/* Navigation + search */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goPrev} data-testid="matrix-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="px-3 py-1.5 rounded-md bg-muted text-sm font-semibold min-w-[160px] text-center" data-testid="matrix-month-label">
                {SPANISH_MONTHS[cursor.month]} {cursor.year}
              </div>
              <Button variant="outline" size="sm" onClick={goNext} data-testid="matrix-next"><ChevronRight className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={goToday}>Mes actual</Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-full md:w-60">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input className="pl-8 h-9" placeholder="Buscar cliente..." value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="matrix-search-client" />
              </div>
              <Select value={responsableFilter || '__all__'} onValueChange={(v) => setResponsableFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="matrix-filter-responsable">
                  <SelectValue placeholder="Responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los responsables</SelectItem>
                  {teamOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={autoFilter || '__all__'} onValueChange={(v) => setAutoFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="matrix-filter-automatizacion">
                  <SelectValue placeholder="Automatización" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Automatización: todas</SelectItem>
                  <SelectItem value="con"><span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-500" /> Con automatización</span></SelectItem>
                  <SelectItem value="sin">Sin automatización</SelectItem>
                </SelectContent>
              </Select>
              <Select value={pilarFilter || '__all__'} onValueChange={(v) => setPilarFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="matrix-filter-pilar">
                  <SelectValue placeholder="Pilar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Pilar: todos</SelectItem>
                  {CONTENT_PILLARS.map((pl) => (
                    <SelectItem key={pl.id} value={pl.id} data-testid={`matrix-pilar-option-${pl.id}`}>
                      <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pl.color }} />{pl.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={() => { setStateFilter(''); setResponsableFilter(''); setAutoFilter(''); setPilarFilter(''); }} data-testid="matrix-clear-filters">
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
          {canConfigure && (
            <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t">
              <span className="text-xs font-medium text-muted-foreground">Responsable de revisión:</span>
              <Select value={reviewerUserId || '__none__'} onValueChange={saveReviewer}>
                <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="reviewer-select">
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {teamOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs font-medium text-muted-foreground ml-3">Responsable de programación:</span>
              <Select value={schedulerUserId || '__none__'} onValueChange={saveScheduler}>
                <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="scheduler-select">
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {teamOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {[['editor_user_id', 'edición', 'Editar'], ['designer_user_id', 'diseño', 'Diseñar']].map(([field, label, stateLabel]) => (
                <React.Fragment key={field}>
                  <span className="text-xs font-medium text-muted-foreground ml-3">Responsable de {stateLabel}:</span>
                  <Select value={roleUsers[field] || '__none__'} onValueChange={(v) => saveRoleUser(field, label, v)}>
                    <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`${field.split('_')[0]}-select`}>
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      {teamOptions.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </React.Fragment>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend with counts — click a state to filter */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="state-filter-legend">
        <button
          onClick={() => setStateFilter('')}
          className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all ${!stateFilter ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-transparent hover:border-muted-foreground/30'}`}
          data-testid="state-filter-all"
        >
          Todos · {totalVisible}
        </button>
        {overdueCount > 0 && (
          <button
            onClick={() => setStateFilter(stateFilter === OVERDUE_FILTER ? '' : OVERDUE_FILTER)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all ${stateFilter === OVERDUE_FILTER ? 'bg-red-600 text-white border-red-600 ring-2 ring-offset-1 ring-red-400' : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'}`}
            data-testid="state-filter-overdue"
            title="Publicaciones cuya fecha ya pasó y no están publicadas"
          >
            <AlertTriangle className="w-3 h-3" /> Atrasadas · {overdueCount}
          </button>
        )}
        {CONTENT_STATES.map((s) => {
          const active = stateFilter === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setStateFilter(active ? '' : s.id)}
              className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium border transition-all ${active ? 'ring-2 ring-offset-1 ring-primary border-transparent' : 'border-transparent hover:opacity-80 hover:scale-[1.03]'}`}
              style={{ backgroundColor: s.bg, color: s.fg }}
              data-testid={`state-filter-${s.id}`}
              title={`Filtrar por ${s.label}`}
            >
              {s.label} · {stateCounts[s.id] || 0}
            </button>
          );
        })}
      </div>

      {user?.role !== 'client' && (
        <div className="flex items-center gap-2" data-testid="publications-download-toolbar">
          <DownloadSelectionBar selection={selection} visiblePosts={visiblePosts} />
        </div>
      )}

      {/* Matrix */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Cargando matriz...</div>
          ) : filteredClients.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">No hay clientes que coincidan.</div>
          ) : (
            <table className="border-collapse text-xs" data-testid="publications-matrix" style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 bg-muted/70 border-b border-r px-2 py-1.5 text-left font-semibold text-muted-foreground" style={{ minWidth: '200px' }}>
                    Cliente
                  </th>
                  {dayList.map((d) => {
                    const dow = dowIndex(cursor.year, cursor.month, d);
                    const isSunday = dow === 6;
                    const isToday = ymd(cursor.year, cursor.month, d) === todayStr;
                    return (
                      <th
                        key={d}
                        className={`border-b border-r px-1 py-1 text-center font-medium ${isSunday ? 'bg-red-50' : 'bg-muted/70'} ${isToday ? 'ring-1 ring-primary ring-inset' : ''}`}
                        style={{ minWidth: '72px' }}
                      >
                        <div className={`text-[10px] ${isSunday ? 'text-red-600' : 'text-muted-foreground'}`}>{DOW_LETTERS[dow]}</div>
                        <div className={`text-sm ${isSunday ? 'text-red-600 font-semibold' : 'text-foreground'}`}>{d}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/10">
                    <td className="sticky left-0 z-10 bg-card border-b border-r px-2 py-1 font-medium truncate" title={c.name} style={{ maxWidth: '220px' }} data-testid={`matrix-row-${c.id}`}>
                      {c.name}
                    </td>
                    {dayList.map((d) => {
                      const dateStr = ymd(cursor.year, cursor.month, d);
                      const dayPosts = matrix[c.id]?.[dateStr] || [];
                      const dow = dowIndex(cursor.year, cursor.month, d);
                      const isSunday = dow === 6;
                      const isToday = dateStr === todayStr;
                      return (
                        <td
                          key={d}
                          className={`border-b border-r p-0.5 align-middle ${isSunday ? 'bg-red-50/40' : ''} ${isToday ? 'ring-1 ring-primary/40 ring-inset' : ''}`}
                          data-testid={`matrix-cell-${c.id}-${dateStr}`}
                        >
                          {dayPosts.length === 0 ? (
                            <div className="text-center text-muted-foreground/50 text-xs py-1">—</div>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {dayPosts.map((p) => {
                                const meta = getStateMeta(p.estado);
                                const overdue = isOverdue(p, todayStr);
                                return (
                                  <div key={p.id} className={`flex items-center gap-0.5 ${overdue ? 'ring-1 ring-red-500 rounded' : ''}`} data-testid={overdue ? `matrix-overdue-${p.id}` : undefined}>
                                    {selection.active && (
                                      <Checkbox
                                        checked={selection.selected.has(p.id)}
                                        onCheckedChange={() => selection.toggle(p.id)}
                                        className="h-3.5 w-3.5 shrink-0"
                                        data-testid={`matrix-select-${p.id}`}
                                      />
                                    )}
                                    {overdue && <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" title="Atrasada: la fecha ya pasó y no está publicada" />}
                                    <Select value={p.estado} disabled={!canEditPost(p)} onValueChange={(v) => updatePost(p.id, { estado: v })}>
                                      <SelectTrigger
                                        className={`flex-1 h-6 px-1.5 py-0 border-0 rounded text-[10px] font-medium leading-tight justify-start gap-0 [&>svg]:hidden ${canEditPost(p) ? 'hover:opacity-80' : 'opacity-100 cursor-default'}`}
                                        style={{ backgroundColor: meta.bg, color: meta.fg }}
                                        data-testid={`matrix-state-${p.id}`}
                                        title={`${meta.label}${p.titulo ? ' · ' + p.titulo : ''}${canEditPost(p) ? '' : ' · Solo lectura (no asignada a ti)'}`}
                                      >
                                        {getPillarMeta(p.pilar) && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: getPillarMeta(p.pilar).color }} title={`Pilar: ${getPillarMeta(p.pilar).label}`} data-testid={`matrix-pilar-${p.id}`} />}
                                        <span className="truncate w-full text-left">{meta.label}</span>
                                        {p.automatizacion && <Zap className="w-2.5 h-2.5 shrink-0" title="Con automatización" data-testid={`matrix-auto-${p.id}`} />}
                                        <ChannelIconsRow post={p} className="w-2.5 h-2.5" colored={false} gap="gap-px" testId={`matrix-channels-${p.id}`} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {CONTENT_STATES.map((s) => (
                                          <SelectItem key={s.id} value={s.id}>
                                            <span className="inline-flex items-center gap-2">
                                              <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: s.bg, border: `1px solid ${s.fg}30` }} />
                                              {s.label}
                                            </span>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <button
                                      onClick={() => openInCalendar(p)}
                                      className="p-0.5 hover:bg-muted rounded"
                                      title="Abrir en el calendario del cliente"
                                      data-testid={`matrix-open-${p.id}`}
                                    >
                                      <ExternalLink className="w-3 h-3 text-muted-foreground" />
                                    </button>
                                    <CommentsBadge info={commentInfo[p.id]} />
                                    <PostDownloadMenu post={p} className="text-muted-foreground" />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

    </motion.div>
  );
}
